import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { translations } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";

const ROTATION_MS = 6000;

// Normalize a hero item to { url, position }
// Accepts either a plain URL string or an object { url, position }.
const normalize = (item) => {
  if (!item) return null;
  if (typeof item === "string") return { url: item, position: "center center" };
  return { url: item.url, position: item.position || "center center" };
};

const Hero = ({ heroImage, heroImages = [] }) => {
  const { lang } = useLang();
  const t = translations[lang].hero;

  const images = (heroImages && heroImages.length > 0
    ? heroImages.map(normalize).filter((x) => x && x.url)
    : heroImage
    ? [{ url: heroImage, position: "center center" }]
    : []
  );

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length < 2) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % images.length), ROTATION_MS);
    return () => clearInterval(id);
  }, [images.length]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid={TEST_IDS.hero.root}
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#0b0d0f]"
    >
      {/* Crossfade slideshow */}
      <AnimatePresence>
        {images.map(
          (img, i) =>
            i === idx && (
              <motion.div
                key={img.url + i}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
                className="absolute inset-0 bg-cover"
                style={{
                  backgroundImage: `url(${img.url})`,
                  backgroundPosition: img.position,
                  filter: "grayscale(0.15) contrast(1.05)",
                }}
                aria-hidden
              />
            ),
        )}
      </AnimatePresence>

      {/* Preload the rest so transitions don't flash */}
      <div style={{ display: "none" }} aria-hidden>
        {images.map((img, i) => (
          <img key={`pre-${i}`} src={img.url} alt="" loading="eager" />
        ))}
      </div>

      <div className="hero-vignette" aria-hidden />
      <div className="hero-noise" aria-hidden />

      <div className="relative rp-container w-full pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-9">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="rp-eyebrow mb-8"
            >
              {t.badge}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold tracking-tight text-white leading-[0.92]"
              style={{ fontSize: "clamp(3rem, 9.2vw, 9rem)" }}
            >
              {t.name.split(" ").map((word, i) => (
                <span key={i} className="block">
                  {word}
                </span>
              ))}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-8 flex items-center gap-4"
            >
              <div className="h-px w-10 bg-[color:var(--rp-accent)]" />
              <p className="font-display text-[13px] md:text-[15px] tracking-[0.24em] text-white/90 uppercase">
                {t.subtitle}
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="mt-6 max-w-[54ch] text-base md:text-lg text-white/70 leading-relaxed"
            >
              “{t.quote}”
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid={TEST_IDS.hero.ctaCareer}
                onClick={() => go("career")}
                className="rp-btn rp-btn-primary"
              >
                {t.viewCareer}
                <ArrowRight size={16} />
              </button>
              <button
                data-testid={TEST_IDS.hero.ctaContact}
                onClick={() => go("contact")}
                className="rp-btn rp-btn-outline"
              >
                {t.contact}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Slide indicator dots */}
      {images.length > 1 && (
        <div className="absolute bottom-6 right-6 z-10 flex gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Hero image ${i + 1}`}
              className={`h-[2px] transition-all ${
                i === idx ? "w-8 bg-[color:var(--rp-accent)]" : "w-4 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[color:var(--rp-muted)]">
        <span className="text-[10px] tracking-[0.32em] uppercase">{t.scroll}</span>
        <div className="h-8 w-px bg-white/20 relative overflow-hidden">
          <span className="rp-scroll-dot absolute top-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-[color:var(--rp-accent)] rounded-full" />
        </div>
        <ArrowDown size={12} />
      </div>
    </section>
  );
};

export default Hero;
